import request from 'src/api'
import { orderByColumnClass } from 'src/datatableControl'
import { customerFunctions } from 'packs/customers/functions'
import { lockButton, unlockButton, hideModalKeepLoader } from 'src/lockButton'
import { handleErrors } from 'src/errors'
import { addToast } from 'src/toasts'
import { assignPresent, camelize, isAdminOrAbove, isRole } from 'src/misc'
import { showGoogleMap } from 'src/googleMap'
import { openDownloadLink } from 'src/download'
import initDatatableChrome from 'src/datatableChrome'
import onIndexRefresh from 'src/indexRefresh'

let customersDatatable

export default () => {
  customerFunctions()

  // set dropdown values from local storage
  const customersIndexSelectBu = localStorage.getItem("customersIndexSelectBu")
  if (customersIndexSelectBu) {
    $("#select-bu").val(customersIndexSelectBu.split(','))
  }
  const customersIndexSelectStatus = localStorage.getItem("customersIndexSelectStatus")
  if (customersIndexSelectStatus) {
    $("#select-status").val(customersIndexSelectStatus)
  }
  const customersIndexSelectDimension = localStorage.getItem("customersIndexSelectDimension")
  if (customersIndexSelectDimension) {
    $("#select-dimension").val(customersIndexSelectDimension)
  }
  
  // 🔑 **The composite role predicate, named once** (`/simplify`, 2026-10-28). It was spelled out four
  // times inside `columnDefs`, and the comment there already makes this argument one level down about
  // `isAdminOrAbove`. Four copies of a role rule is four chances for a column to reach the wrong role.
  const salesOrAdmin = isRole('MYYJÄ') || isAdminOrAbove()

	// datatable init
  customersDatatable = $('#customers-datatable').DataTable({
    "language": {
      url: "/datatable_i18n"
    },
    "pageLength": (localStorage.getItem('customersIndexPageLength') || 25),
    "processing": true,
    // TODO.md F-169. Derived, because the id column took index 0 and `order` accepts indices only.
    "order": orderByColumnClass('customers-datatable', 'company-name-col'),
    "serverSide": true,
    "ajax": {
      "url": $('#customers-datatable').data('source'),
      "data": function (d) {
        assignPresent(d, {
          bu: $('#select-bu').val(),
          status: $('#select-status').val(),
          dimension: $('#select-dimension').val()
        })
      }
    },
    "columns": [
      { "data": "id" },
      { "data": "company_name" },
      { "data": "location_name" },
      { "data": "business_id" },
      { "data": "dimension" },
      { "data": "business_unit" },
      { "data": "industry" },
      // TODO.md U-76. `address-col` replaces a `nth-child(8)` CSS selector that had already shifted twice —
      // 7 → 8 when F-123 inserted the control column, and back again when F-168 removed it, neither of
      // which this class noticed. `columns.className` lands on BOTH the th (`jquery.dataTables.js:2319`)
      // and the td (`:3201`), which is what the min-width rule needs.
      { "data": "address", "className": "address-col" },
      { "data": "contact_person" },
      { "data": "phone" },
      { "data": "email" },
      { "data": "status" },
      // TODO.md U-116. The seller. ⚠️ **A column is FOUR layers, not three** — the `<th>`, this entry, the
      // datatable's `view_columns` and its `data` hash. Adding the first three and omitting this one makes
      // DataTables throw on init, so `initComplete` never runs and `src/datatableChrome.js` never
      // relocates the toolbar: the symptom is the *buttons* disappearing, not the column.
      { "data": "salesperson" },
      { "data": "contract_approved_date" },
      { "data": "contract_start_date" },
      { "data": "created_at" },
      { "data": "actions", "orderable": false, "className": "all" }
    ],
    "columnDefs": [
      // TODO.md F-123 / U-76 / F-168. Targeted by CLASS, not index. It was `targets: [5]`, F-123's control
      // column at index 0 shifted it to 6, and F-168 removing that column shifted it back — three moves of
      // a number that goes stale silently, and the class has needed no edit through any of them.
      // DataTables resolves a string target with `hasClass` on the TH (`jquery.dataTables.js:2635-2642`).
      // ⚠️ This is `industry`; getting it wrong hides `business_unit` from salespeople instead and nothing
      // would report it.
      // ⚠️ `contract-start-date-col` keeps `!isRole('MYYJÄ')` deliberately: it is NOT the complement of
      // `salesOrAdmin`, and the matrix below is why.
      { targets: 'industry-col', visible: salesOrAdmin },
      // 🔑 TODO.md U-116. The seller column — MYYJÄ and ADMIN/SUPER_ADMIN, which `salesOrAdmin` already
      // names. ⚠️ Its `searchable:` twin is `sees_salesperson?` in `customers_datatable.rb`; the two are
      // one rule in two languages and `spec/config/datatable_responsive_spec.rb` asserts they agree.
      { targets: 'salesperson-col', visible: salesOrAdmin },
      // 🔑 TODO.md F-169. The owner's matrix, 2026-10-26 — and the two DATE rules are NOT one rule and its
      // negation, which is the mistake this comment exists to prevent:
      //
      //            id   approved   start   created_at   industry
      //   MYYJÄ    yes    yes       NO        yes          yes
      //   ADMIN+   yes    yes       yes       yes          yes
      //   others   no     no        yes       no           no
      //
      // 📌 `industry` joined the admin side on 2026-10-27 at the owner's request. It had been
      // MYYJÄ-only since `afb0362a` (2026-01-26) — nothing to do with F-169, which only inherited it.
      //
      // Approved is `MYYJÄ || admin-or-above`; start is `!MYYJÄ`. They OVERLAP on admin, so writing one as
      // the complement of the other silently drops the start date for admins.
      // 📌 `isAdminOrAbove` rather than `isRole('ADMIN')`: `isRole` is an exact match, so a SUPER_ADMIN
      // would otherwise fall into "others" and lose three columns. The owner confirmed SUPER_ADMIN sees
      // what ADMIN sees — the predicate is named once in `src/misc.js` rather than spelled out here three
      // times.
      { targets: 'customer-id-col', visible: salesOrAdmin },
      { targets: 'customer-created-at-col', visible: salesOrAdmin },
      { targets: 'contract-approved-date-col', visible: salesOrAdmin },
      { targets: 'contract-start-date-col', visible: !isRole('MYYJÄ') }
    ],
    initComplete(settings, json) {
      initDatatableChrome('customers-datatable')

      $('#select-bu').select2({
        minimumInputLength: 0,
        placeholder: I18n.t("selects.select_business_unit"),
        width: '100%'
      })
  	}
  })

  // T-102 / T-108 / U-48: reload this page's table when a save says so. The `off()`/`on()` pair and
  // `resetPaging: false` live in `src/indexRefresh` — five packs carried a copy of both.
  onIndexRefresh('customersIndex', customersDatatable)

  $('#content').on('change', '#select-bu, #select-status, #select-dimension', function() {
    localStorage.setItem(camelize('customers-index-' + $(this).attr('id')), $(this).val())
    customersDatatable.ajax.reload()
  })

  $('#content').on('change', "[name='customers-datatable_length']", function() {
    localStorage.setItem('customersIndexPageLength', $(this).val())
  })

  $('.clear-button').on('click', function() {
    $('.clear-button').addClass('d-none')
    customersDatatable.search('').draw()
  })

  $('#content').on('click', '#add_customer', function() {
    lockButton('#add_customer')
    offcanvasRight.show()
    $.getScript('/customers/new')
  })

  $('#content').on('click', '#import_customers', function() {
    lockButton('#import_customers')
    $.getScript('/customers/import_customers_modal')
      .then(() => unlockButton('#import_customers'))
      .catch(err => handleErrors(err, '#import_customers'))
  })

  $('#mainModal').on('click', '#confirmImportCustomers', async function() {
    const files = $('#importCustomersFile')[0].files
    if (files.length === 0) {
      addToast(I18n.t("errors.no_file"), 'alert')
      return
    }

    lockButton('#confirmImportCustomers')

    try {
      const res = await request.post('/customers/import_customers')
        .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
        .accept('json')
        .attach('file', files[0])
        .field('send_starttilomake', $('#sendStarttilomake').is(':checked'))
        .field('send_to_nav', $('#sendToNav').is(':checked'))

      hideModalKeepLoader()

      const { result } = await waitForJob(res.body.jobId, { intervalMs: 1000, timeoutMs: 120000 })

      if (result === 1) {
        addToast(I18n.t("import_customers_modal.success"), 'notice')
        customersDatatable.ajax.reload()
      } else {
        const jobRes = await request.get(`/background_jobs/${res.body.jobId}`)
          .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
          .accept('json')
        const error = I18n.t("import_customers_modal.error") + '\n' + jobRes.body.error_message
        addToast(error, 'error')
      }
    } catch (err) {
      handleErrors(err, '#confirmImportCustomers')
    } finally {
      unlockButton('#confirmImportCustomers')
    }
  })

  const listParams = () => {
    let p = 'status=' + $('#select-status').val() + '&dimension=' + $('#select-dimension').val() + '&query=' + $('#search-div input').val()
    if ($('#select-bu').length) p += '&bu=' + $('#select-bu').val()
    return p
  }

  // 📌 **The `#own_booking` click handler is GONE with its button (`U-116`)** — that was the MYYJÄ-only
  // shortcut. The modal's `own_booking` option reaches the same endpoint, through the branch at the
  // bottom of this file, so nothing is lost but the second entry point.

  const startBackgroundDownload = async (btn, url) => {
    lockButton(btn)
    fullscreenLoader.show()
    try {
      const res = await request.get(url)
        .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
        .accept('json')
      await waitForJob(res.body.job_id, { intervalMs: 1000, timeoutMs: 120000 })
      const jobRes = await request.get(`/background_jobs/${res.body.job_id}`)
        .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
        .accept('json')
      if (jobRes.body.file_url) {
        openDownloadLink(jobRes.body.file_url, { newTab: jobRes.body.open_in_tab })
        addToast(I18n.t('download.ready'), 'notice')
      }
    } catch (err) {
      handleErrors(err, btn)
    } finally {
      fullscreenLoader.hide()
      unlockButton(btn)
    }
  }

  // currentTarget, not target: both buttons wrap an <i> icon, so a click on the icon made target the
  // <i>, whose id is undefined -- the request went to /customers/undefined_list and 404'd with no
  // download (TODO-ARCHIVE.md F-86). currentTarget is the element the selector matched, always.
  $('#content').on('click', '#customers, #contact_people', function(event) {
    const urlKey = event.currentTarget.id
    startBackgroundDownload(event.currentTarget, '/customers/' + urlKey + '_list?' + listParams())
  })

  let _notesBtn = null

  const doNotesDownload = (btn, userId) => {
    let q = '/customers/notes_list?' + listParams()
    if (userId && userId !== 'all') q += '&user_id=' + userId
    startBackgroundDownload(btn, q)
  }

  // 🔑 TODO.md U-116. **Every role opens the modal.** A MYYJÄ used to be sent straight to a notes
  // download via `data-is-myyja`, so the only way they could get an own-booking list was a separate
  // button — which is the button this removed. Owner, 2026-11-04: *"muistiinpanot button should show
  // similar modal as for admins to select what type of list except no user select"*.
  // 📌 **No role branch here and none needed**: the modal omits the user select when there is nobody to
  // pick (the view renders it only if `@notes_users.any?`, and `notes_modal` leaves that empty for every
  // role but SUPER_ADMIN and ADMIN), and `$('#notes-user-select').val()` is then `undefined`, which the
  // download handler's `if (userId && userId !== 'all')` already skips.
  $('#content').on('click', '#notes', function(event) {
    _notesBtn = event.currentTarget
    lockButton('#notes')
    $.getScript('/customers/notes_modal')
      .then(() => unlockButton('#notes'))
      .catch(err => handleErrors(err, '#notes'))
  })

  // TODO.md F-179. The two customer flags are per CUSTOMER, so "whose notes" means nothing for them.
  //
  // ⚠️ **Reset the value as well as disabling it.** The click handler below reads `.val()`, not the
  // enabled state, so a `user_id` left selected would still reach the query string — `disabled` keeps it
  // out of a FORM submit, and this is not a form submit.
  // 📌 Disabled rather than hidden: a control that vanishes and returns is harder to follow than one that
  // greys out, which is the precedent `F-174` set for *Vahvistettu*.
  //
  // 🔑 **Read off the OPTION, never from a copy of the list here.** This was
  // `['public_sector', 'reference_customer']` — a third copy of a set that also lives in
  // `CustomersListJob::FLAG_FILTERS` and in the view, which rendered both values as literals. The view now
  // renders its options from the constant and marks each with `data-per-customer`, so a new flag never
  // reaches this file. ⚠️ `.data('perCustomer')` — jQuery camel-cases `data-per-customer`, and it coerces
  // the string `"true"` to a boolean.
  const isPerCustomer = () => $('#notes-list-type-select').find(':selected').data('perCustomer') === true

  $('#mainModal').on('change', '#notes-list-type-select', function() {
    const perCustomer = isPerCustomer()
    const $user = $('#notes-user-select')
    if (perCustomer) $user.val('all')
    $user.prop('disabled', perCustomer)
  })

  // 🚨 **READ EVERY VALUE BEFORE `mainModal.hide()` — the hide DESTROYS the form.**
  // `packs/application.js`'s `hidden.bs.modal` handler replaces `#mainModal`'s entire HTML with an empty
  // shell, so after the hide `$('#notes-list-type-select')` matches NOTHING and `.data('perCustomer')` is
  // `undefined`. ⚠️ **That shipped for a few hours on 2026-10-30**: `/simplify` replaced an array
  // `includes(listType)` test — which read a local captured before the hide — with a DOM read placed
  // after it, so both flag lists silently fell through to `doNotesDownload` and downloaded notes. The
  // owner found it; no spec drives this click.
  // 🔑 `listType` was captured up here all along, which is exactly why the old form could not break this
  // way. The flag now is too.
  $('#mainModal').on('click', '#confirmDownloadNotes', function() {
    const listType = $('#notes-list-type-select').val()
    const perCustomer = isPerCustomer()
    const userId = $('#notes-user-select').val()
    mainModal.hide()
    if (perCustomer) {
      startBackgroundDownload(_notesBtn, '/customers/customers_list?' + listParams() + '&only=' + listType)
    } else if (listType === 'own_booking') {
      let q = '/customers/own_booking_list?' + listParams()
      if (userId && userId !== 'all') q += '&user_id=' + userId
      startBackgroundDownload(_notesBtn, q)
    } else {
      doNotesDownload(_notesBtn, userId)
    }
  })

  $('#content').on('click', '#map', function(event) {
    lockButton(event.currentTarget)

    let url = '/customers/address_list?' + listParams()

    request.get(url)
      .timeout({ response: 60000, deadline: 120000 })
      .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
      .accept('json')
      .then(res => {
        let addresses = []
        $.each(res.body.addresses, function(index, value) {
          const totalAddress = (value.street_name + ', ' + value.post_code + ' ' + value.town).trim()
          let title = value.company_name
          if (value.location_name) {
            title += ', ' + value.location_name
          }
          if (totalAddress.length > 1) {
            addresses.push({ address: totalAddress + ', ' + value.country, title: title + "\n" + totalAddress, color: value.color })
          }
        })

        unlockButton(event.currentTarget)
        if (addresses.length) {
          showGoogleMap(mainModal, '', addresses)
        } else {
          addToast(I18n.t("errors.no_addresses"), 'alert')
        }
      })
      .catch(err => {
        handleErrors(err, event.currentTarget)
      })
  })

  $('#content').on('click', '.show-googlemap', function(event) {
    $(this).prop("disabled", true)

    const companyName = $(this).data('company_name')
    const locationName = $(this).data('location_name')
    const streetName = $(this).data('street_name')
    const postCode = $(this).data('post_code')
    const town = $(this).data('town')
    const country = $(this).data('country')
    const totalAddress = (streetName + ", " + postCode + " " + town).trim()
    if (totalAddress.length > 1) {
      let title = companyName
      if (locationName) {
        title += ', ' + locationName
      }

      const clientId = $(this).data('client_id')
      const buId = $(this).data('business_unit_id')
      request.get('/clients/' + clientId + '/business_units?id=' + buId)
      .set('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
      .accept('json')
      .then(res => {
        showGoogleMap(mainModal, title, [{ address: totalAddress + ", " + country, color: res.body[0] && res.body[0].color }])
        $(this).prop("disabled", false)
      })
      .catch(err => {
        handleErrors(err, this)
      })
    } else {
      addToast(I18n.t("errors.no_address"), 'alert')
    }

    $(this).prop("disabled", false)
  })
}

